import { createSelector } from '@reduxjs/toolkit'
import { selectLoanData } from '../loan/loanSelectors'
import { selectIsAmcLender, selectHasPermission } from '../user/userSelectors'
import STATUS from '../../../common/constants/reducer_statuses'
import { JOB_TYPES } from '../../../common/constants/jobTypes'
import { OFFER_STATUSES } from '../../../common/constants/offer'

export const selectMinimumOrderStatus = (state) => state.order.minimumStatus

export const selectRemainingOrderStatus = (state) => state.order.remainingStatus

export const selectOrderTabs = (state) => state.order.orderTabs

export const selectMinimumOrder = (state) => state.order.minimumOrder

export const selectRemainingOrder = (state) => state.order.remainingOrder

export const selectOrderTimeline = (state) => state.order.orderTimeline

export const selectTimelineStatus = (state) => state.order.timelineStatus

export const selectOrderSchedule = (state) => state.order.minimumOrder.schedule

export const selectPaymentAllowed = (state) => state.order.paymentAllowed

export const selectPaymentAllowedStatus = (state) => state.order.paymentAllowedStatus

// Common selectors for loading and error states
export const selectOrderIsLoading = (state) => state.order.minimumStatus === STATUS.LOADING

export const selectOrderHasError = (state) => state.order.minimumStatus === STATUS.FAILED

export const selectOrderError = (state) => state.order.error

export const selectOrderLoadingAndErrorState = (state) => ({
  isLoading: state.order.minimumStatus === STATUS.LOADING,
  hasError: state.order.minimumStatus === STATUS.FAILED,
})

export const selectLoanId = (state) => {
  const minimumOrder = selectMinimumOrder(state)
  return minimumOrder?.loan_file?.id ?? null
}

export const selectSubmissions = (state) => selectRemainingOrder(state).submissions || []

export const selectIsSubmissionDataIncomplete = (state) => {
  const submissions = selectSubmissions(state)
  return submissions.length === 0 || submissions.some((submission) => submission.isIncomplete)
}

export const selectCurrentSubmission = createSelector(
  [selectSubmissions, (state, submissionVersion) => submissionVersion],
  (submissions, submissionVersion) => {
    const parsedVersion = Number(submissionVersion)

    if (!parsedVersion || !submissions?.length) {
      return submissions?.[submissions?.length - 1]
    }

    const currentSubmission = submissions.find((sub) => sub.version === parsedVersion)

    return currentSubmission || submissions?.[submissions?.length - 1]
  },
)

export const selectPreviousSubmission = createSelector(
  [selectSubmissions, (state, submissionVersion) => submissionVersion],
  (submissions, submissionVersion) => {
    const parsedVersion = Number(submissionVersion)

    if (!parsedVersion || parsedVersion === 1 || !submissions?.length) {
      return undefined
    }

    const previousVersion = parsedVersion - 1
    return submissions.find((sub) => sub.version === previousVersion)
  },
)

export const selectOrderSupportsRepurchaseWarranty = createSelector([selectMinimumOrder], (data) => {
  const result = data?.order?.products?.some((product) => product?.run_warranty_review)
  return result
})

export const selectActiveOrderTab = (state) => state.order.activeOrderTab

export const selectPrimaryOrderId = (state) => state.order.primaryOrderId

export const selectIsPrimaryOrder = (state) => selectActiveOrderTab(state) === selectPrimaryOrderId(state)

export const selectAllOrderData = createSelector(
  selectMinimumOrder,
  selectRemainingOrder,
  (minimumOrder, remainingOrder) => {
    const minimumOrderData = minimumOrder?.order || {}
    return { ...minimumOrderData, ...remainingOrder }
  },
)

/**
 * Selects the current order ID
 */
export const selectOrderId = createSelector([selectMinimumOrder], (orderData) => orderData?.order?.id)

export const selectLatestLenderAttention = (state, lenderId, reasons) => {
  let lenderAttentions = selectAllOrderData(state).lender_attentions || []
  if (lenderId) {
    lenderAttentions = lenderAttentions.filter((lenderAttention) => lenderAttention.lender_id === lenderId)
  }
  if (reasons && reasons.length > 0) {
    lenderAttentions = lenderAttentions.filter((lenderAttention) => reasons.includes(lenderAttention.reason))
  }
  return lenderAttentions[0] || {}
}

export const selectOrderReviewEngineConfig = (state) => {
  return selectAllOrderData(state)?.review_engine || {}
}

export const selectOrderSubmissionReviewRulesetForUser = (state) => {
  const isAmcLender = selectIsAmcLender(state)
  const reviewEngineConfig = selectOrderReviewEngineConfig(state)
  if (!reviewEngineConfig) {
    return null
  }
  const {
    rule_sets, // new
    rule_set_keys, // old
  } = reviewEngineConfig
  if (rule_sets) {
    const targetRuleSetType = isAmcLender ? 'amp' : 'lender'
    const ruleSet = rule_sets?.find((ruleSet) => ruleSet?.rule_set_type === targetRuleSetType)
    if (ruleSet) {
      return ruleSet.rule_set_key
    }
  }
  if (rule_set_keys) {
    return rule_set_keys?.[0]
  }
}

export const selectOffer = (state, assignmentListRequestId, offerId) => {
  const orderData = selectAllOrderData(state)
  const { assignment_list } = orderData
  if (!assignment_list) {
    return null
  }
  const assignmentListRequest = assignment_list.find((request) => request.id === assignmentListRequestId)
  if (!assignmentListRequest) {
    return null
  }
  const { offers } = assignmentListRequest
  if (!offers) {
    return null
  }
  return offers.find((offer) => offer.id === offerId)
}

// Assignment-list statuses whose offers are still live. Mirrors the backend filter in
// minimum_lender_order_page_to_json2 that builds `assignment_list_request`.
const LIVE_ASSIGNMENT_LIST_STATUSES = ['active', 'company_accepted']

const isPendingClientLender = (offer) => offer.is_pending_client_lender === true

/**
 * The escalation offer the current viewer is expected to act on, together with the
 * assignment_list request that owns it.
 *
 * `orderData.assignment_list_request` is only ever the FIRST live request in document
 * order (see minimum_lender_order_page_to_json2). That is the right request when an
 * order has a single live request, but a cascade or broadcast order can have several
 * live at once, each carrying its own pending offer — and the offer forwarded to the
 * client lender is frequently not in the first one. Reading only that request hides
 * the banner on exactly the orders where a decision is waiting. Scanning
 * assignment_list is how AppraiserRequestTab and the comment-template helpers already
 * resolve the same question.
 *
 * Returning the owning request alongside the offer is not cosmetic: accept / deny /
 * defer all post `assignment_list_request_id` and `offer_id` together, so the pair has
 * to come from the same request.
 */
export const selectEscalationOfferContext = createSelector(
  [selectAllOrderData, selectIsAmcLender],
  (orderData, isAmcLender) => {
    const {
      assignment_list: assignmentList,
      assignment_list_request: serializedRequest,
      job_type: jobType,
    } = orderData

    const pairsFrom = (requests) => requests
      .filter(Boolean)
      .flatMap((request) => (request.offers || []).map((offer) => ({ request, offer })))

    const liveRequests = Array.isArray(assignmentList)
      ? assignmentList.filter(({ status }) => LIVE_ASSIGNMENT_LIST_STATUSES.includes(status))
      : []

    // Prefer the wide scan, but fall back to the single serialized request whenever it
    // turns up no offers at all — not every payload carries assignment_list, and some
    // carry it only for the vendor-name lookup in AlertMessage. On a real lender order
    // payload assignment_list IS the live-filtered list the backend picked
    // assignment_list_request out of, so the wide scan is a superset of the old
    // behaviour and this fallback never fires.
    const livePairs = pairsFrom(liveRequests)
    const pairs = livePairs.length ? livePairs : pairsFrom([serializedRequest])
    const pending = pairs.filter(({ offer }) => offer.status === OFFER_STATUSES.PENDING)
    const processing = pairs.filter(({ offer }) => offer.status === OFFER_STATUSES.PROCESSING)

    // On an AMC order a pending offer's decision belongs to exactly one side: the client
    // lender owns it once AMP has forwarded it (is_pending_client_lender), AMP owns it
    // before that. Rank the candidates by who can act, most actionable first, and take
    // the head. Each branch keeps pending ahead of processing for the viewer that owns
    // it, exactly as the single-request selectPendingOrProcessingOffer did.
    const forwarded = pending.filter(({ offer }) => isPendingClientLender(offer))
    const notForwarded = pending.filter(({ offer }) => !isPendingClientLender(offer))

    let ordered
    if (jobType !== JOB_TYPES.AMC) {
      // No client-lender forwarding on a non-AMC order.
      ordered = [...pending, ...processing]
    } else if (isAmcLender) {
      // AMP acts on what it has not forwarded yet. A forwarded offer still earns AMP's
      // "Pending Lender" tag, so it outranks a processing offer AMP cannot act on.
      ordered = [...notForwarded, ...forwarded, ...processing]
    } else {
      // The client lender acts on what AMP forwarded, or on a processing offer it owes
      // payment for. An offer AMP has not forwarded is none of its business, so it goes
      // last — and if it is all that is left, selectEscalationBannerVisible hides the
      // banner rather than showing the lender someone else's decision.
      ordered = [...forwarded, ...processing, ...notForwarded]
    }

    return ordered[0] || { request: null, offer: null }
  },
)

/**
 * Whether the yellow fee-escalation banner will actually render for this viewer.
 *
 * Single source of truth shared by FeeEscalationAlert (which returns null when this is
 * false) and the red Attention-Required strip, whose "Review escalation" button scrolls
 * to that banner. Previously the strip only knew the order payload had finished
 * loading, so it could offer a button that scrolled to nothing.
 *
 * Callers still have to AND in their own gates: the banner only mounts once the
 * remaining order has loaded and an escalation LenderAttention exists.
 */
export const selectEscalationBannerVisible = createSelector(
  [selectAllOrderData, selectIsAmcLender, selectEscalationOfferContext],
  (orderData, isAmcLender, { offer }) => {
    if (!offer) {
      return false
    }

    const amcOrder = orderData.job_type === JOB_TYPES.AMC
    const offerProcessing = offer.status === OFFER_STATUSES.PROCESSING
    const pendingClientLenderOrProcessing = offerProcessing || isPendingClientLender(offer)

    // -- AMC order logic --
    // For the client lender, only show the alert if the offer is pending client OR processing.
    // For AMP, hide the alert if the offer is still processing (awaiting payment from the
    // client lender).
    if (amcOrder && (
      (!isAmcLender && !pendingClientLenderOrProcessing) ||
      (isAmcLender && offerProcessing)
    )) {
      return false
    }

    // AlertMessage only has a body to render for these two statuses.
    return offer.status === OFFER_STATUSES.PENDING || offerProcessing
  },
)

export const selectLoanOrderData = (state) => selectMinimumOrder(state)?.loan_file || selectLoanData(state) || {}

/**
 * Selects the UCDP submissions array from loan data
 */
export const selectUcdpSubmissions = createSelector(
  [selectLoanOrderData],
  (loanData) => loanData?.ucdp_submissions || [],
)

export const selectPropertyAddress = (state) => {
  const orderData = selectAllOrderData(state)
  const loanData = selectLoanOrderData(state)

  let address = ''
  if (orderData && orderData.subject_property) {
    const property = orderData.subject_property
    address = `${property.property_address_full}`
    if (property.property_address_city) {
      address += `, ${property.property_address_city}`
    }
    if (property.property_address_state) {
      address += `, ${property.property_address_state}`
    }
    if (property.property_address_zip) {
      address += ` ${property.property_address_zip}`
    }
  } else if (orderData && orderData.manual_subject_property) {
    const property = orderData.manual_subject_property
    address = `${property.property_address_full}`
    if (property.property_address_city) {
      address += `, ${property.property_address_city}`
    }
    if (property.property_address_state) {
      address += `, ${property.property_address_state}`
    }
    if (property.property_address_zip) {
      address += ` ${property.property_address_zip}`
    }
  } else {
    address = `${loanData.subject_property_address}`
    if (loanData.subject_property_city) {
      address += `, ${loanData.subject_property_city}`
    }
    if (loanData.subject_property_state) {
      address += `, ${loanData.subject_property_state}`
    }
    if (loanData.subject_property_zip) {
      address += ` ${loanData.subject_property_zip}`
    }
  }

  return address
}

export const selectCurrentVeteranId = (state) => {
  const loanData = selectLoanOrderData(state)
  const veteranConsumer = loanData?.consumers?.find((consumer) => consumer.is_veteran)
  return veteranConsumer?.id || null
}

export const selectCurrentPrimaryObligorId = (state) => {
  const loanData = selectLoanOrderData(state)
  const primaryObligorConsumer = loanData?.consumers?.find((consumer) => consumer.is_primary_obligor)
  return primaryObligorConsumer?.id || null
}

export const selectIsUsingAmcLender = (state) => {
  const orderData = selectAllOrderData(state)
  const isAmcLender = selectIsAmcLender(state)

  return !isAmcLender && orderData?.job_type === 'amc'
}

export const selectShowRevisions = (state) => {
  const orderData = selectAllOrderData(state)
  const hasRevisionPermission = selectHasPermission(state, 'revisions_view')
  const { submissions } = orderData
  const hasSubmissions = submissions && submissions.length > 0

  return hasSubmissions && hasRevisionPermission
}

export const selectShowSubmissions = (state) => {
  // get relevant data
  const orderData = selectAllOrderData(state)
  const hasSubmissionPermission = selectHasPermission(state, 'submission_view')
  const { submissions } = orderData
  const hasSubmissions = submissions && submissions.length > 0

  return hasSubmissions && hasSubmissionPermission
}

export const selectOrderAssignedStatus = (state) => state.order.orderAssignedStatus
export const selectOrderAssignedError = (state) => state.order.orderAssignedError
export const selectOrderAssignedErrorStatus = (state) => state.order.orderAssignedErrorStatus

export const selectFollowUpAllocationData = (state) => state.order.followUpAllocationData

export const selectOrderAuditLogData = (state) => state.order.orderAuditLogData

/**
 * Helper function to find GSE submission by type
 * @param {Array} ucdpSubmissions - Array of GSE submissions
 * @param {string} gseType - The GSE type to filter by ('UCDP' or 'EAD')
 * @param {number} submissionVersion - The submission version to match
 * @param {string} orderId - The order ID to match
 * @returns {Object|null} The matching submission or null if not found
 */
const findGseSubmissionByType = (ucdpSubmissions, gseType, submissionVersion, orderId) => {
  let currentSubmission = null

  for (const submission of ucdpSubmissions) {
    // Skip testing results
    if (submission.pathway_result?.status === 'testing') {
      continue
    }

    // Only process specified GSE type
    if (submission.which_gse !== gseType) {
      continue
    }

    // Check if submission matches the current version and order
    const appraisals = submission.pathway_request?.appraisals || []
    for (const submittedAppraisal of appraisals) {
      if (submissionVersion === submittedAppraisal.submission_version && orderId === submittedAppraisal.order_id) {
        currentSubmission = submission
      }
    }
  }

  return currentSubmission
}

/**
 * Selects the current UCDP submission for a given submission version
 * Uses the current order's ID from the state automatically
 * @param {Object} state - Redux state
 * @param {number} submissionVersion - The submission version to match
 * @returns {Object|null} The matching UCDP submission or null if not found
 */
export const selectCurrentUcdpSubmission = createSelector(
  [selectUcdpSubmissions, selectOrderId, (state, submissionVersion) => submissionVersion],
  (ucdpSubmissions, orderId, submissionVersion) => {
    return findGseSubmissionByType(ucdpSubmissions, 'UCDP', submissionVersion, orderId)
  },
)

/**
 * Selects the current EAD submission for a given submission version
 * Uses the current order's ID from the state automatically
 * @param {Object} state - Redux state
 * @param {number} submissionVersion - The submission version to match
 * @returns {Object|null} The matching EAD submission or null if not found
 */
export const selectCurrentEadSubmission = createSelector(
  [selectUcdpSubmissions, selectOrderId, (state, submissionVersion) => submissionVersion],
  (ucdpSubmissions, orderId, submissionVersion) => {
    return findGseSubmissionByType(ucdpSubmissions, 'EAD', submissionVersion, orderId)
  },
)

/**
 * Selects the P&S data from the remaining order
 * @param {Object} state - Redux state
 * @returns {Object|null} The P&S object containing documents array and other P&S data
 */
export const selectPAndSData = createSelector([selectRemainingOrder], (remainingOrder) => remainingOrder?.p_and_s)

/**
 * Selects the evault data from the remaining order
 * @param {Object} state - Redux state
 * @returns {Object|null} The evault object containing documents array and other evault data
 */
export const selectEvaultData = createSelector(
  [selectRemainingOrder],
  (remainingOrder) => remainingOrder?.evault || remainingOrder?.amc_evault,
)

export const selectInspectionAvailabilityData = (state) => state.order.inspectionAvailabilityData
export const selectInspectionAvailabilityStatus = (state) => state.order.inspectionAvailabilityStatus
export const selectInspectionAvailabilityError = (state) => state.order.inspectionAvailabilityError
